import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function InsuranceCompanySelect({ value, onChange, label = 'Insurance Company', disabled }) {
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCompany, setNewCompany] = useState({ company_name: '', address1: '', city_st_zip: '' });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync value prop
  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/insurance-companies?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOptions(data.slice(0, 8));
    } catch {
      setOptions([]);
    }
    setLoading(false);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    search(v);
    onChange(null, v, '', '', ''); // free text
  };

  const select = (opt) => {
    setQuery(opt.company_name);
    setShowDropdown(false);
    onChange(opt.id, opt.company_name, opt.address1 || '', opt.city_st_zip || '', opt.original_id || '');
  };

  const addNew = async () => {
    if (!newCompany.company_name.trim()) return;
    const res = await fetch(`${API_URL}/api/insurance-companies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(newCompany)
    });
    const created = await res.json();
    select(created);
    setShowAddForm(false);
    setNewCompany({ company_name: '', address1: '', city_st_zip: '' });
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { setShowDropdown(true); search(query || ''); }}
          disabled={disabled}
          placeholder="Type to search or select..."
          className={`w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${disabled ? 'bg-gray-50' : ''}`}
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setShowDropdown(v => !v); if (!showDropdown) search(query || ''); }}
          disabled={disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>}
          {!loading && options.map(opt => (
            <div key={opt.id} onMouseDown={() => select(opt)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm">
              {opt.company_name}
              {opt.city_st_zip && <span className="text-xs text-gray-400 ml-2">{opt.city_st_zip}</span>}
            </div>
          ))}
          {!loading && options.length === 0 && query.length >= 1 && (
            <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
          )}
          <div className="border-t px-3 py-2">
            {!showAddForm ? (
              <button onMouseDown={() => setShowAddForm(true)} className="text-xs text-blue-600 hover:underline">+ Add New Company</button>
            ) : (
              <div className="space-y-1">
                <input
                  value={newCompany.company_name}
                  onChange={e => setNewCompany(p => ({ ...p, company_name: e.target.value }))}
                  placeholder="Company name *"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <input
                  value={newCompany.address1}
                  onChange={e => setNewCompany(p => ({ ...p, address1: e.target.value }))}
                  placeholder="Address"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <input
                  value={newCompany.city_st_zip}
                  onChange={e => setNewCompany(p => ({ ...p, city_st_zip: e.target.value }))}
                  placeholder="City, State Zip"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <div className="flex gap-1">
                  <button onMouseDown={addNew} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Add</button>
                  <button onMouseDown={() => setShowAddForm(false)} className="px-2 py-1 text-xs bg-gray-200 rounded">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
